/**
 * Task #245 — shared renderer that wraps `<mark>` around match indices.
 * Same component is used for the primary project-name line, the
 * secondary match-reason line, and the keyword chip. One contract, one
 * renderer — never branch on tier.
 */

import { cn } from '@/lib/utils';

interface Props {
  text: string;
  indices: Array<[number, number]>;
  className?: string;
  markClassName?: string;
}

export function HighlightedText({ text, indices, className, markClassName }: Props) {
  if (!indices || indices.length === 0) {
    return <span className={className}>{text}</span>;
  }
  // Sort + collapse overlaps defensively.
  const ranges = [...indices].sort((a, b) => a[0] - b[0]);
  const pieces: Array<{ text: string; mark: boolean }> = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start > cursor) pieces.push({ text: text.slice(cursor, start), mark: false });
    pieces.push({ text: text.slice(start, end + 1), mark: true });
    cursor = end + 1;
  }
  if (cursor < text.length) pieces.push({ text: text.slice(cursor), mark: false });

  return (
    <span className={className}>
      {pieces.map((p, i) =>
        p.mark ? (
          <mark
            key={i}
            className={cn(
              'rounded-sm bg-yellow-400/40 px-0.5 font-medium text-white',
              markClassName,
            )}
          >
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </span>
  );
}
